/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {Alert, Image, Stack, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import {AlertIcon} from '@salesforce/retail-react-app/app/components/icons'
import StandardLogin from '../../components/standard-login'
import PasswordlessLogin from '@salesforce/retail-react-app/app/components/passwordless-login'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {loginDescriptionTextStyle, loginFormStyle, loyaltyBenefitsStyle} from './index.style'
import ContentRenderer from '../content-renderer'
import {brandLogo, loyaltyHeader} from '../register/index.style'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {pxToRem} from '../../utills/pxToRem'

const LoginForm = ({
    hidePassword,
    hideEmail,
    submitForm,
    handleEmailSubmit,
    handleEditEmailClick,
    isCustomerSearchLoading,
    handleForgotPasswordClick,
    handlePasswordlessLoginClick,
    // clickCreateAccount = noop,
    form,
    isPasswordlessEnabled = false,
    isSocialEnabled = false,
    idps = [],
    setLoginType
}) => {
    return (
        <Fragment>
            <Stack justify="center" align="center" spacing={8}>
                {/* brand logo */}
                <Image
                    sx={brandLogo}
                    src={getAssetUrl('static/img/global/My_Cavenders-Logo.png')}
                />

                {/* loyalty message */}
                <Text sx={loyaltyHeader} as={'h2'} align="center">
                    {!hideEmail ? (
                        <FormattedMessage
                            defaultMessage="Loyalty is rewarded here"
                            id="register_form.heading.loyalty_is_rewarded_here"
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="Welcome Back!"
                            id="login_form.heading.welcome_back"
                        />
                    )}
                </Text>
                {!hidePassword && (
                    <Text {...loginDescriptionTextStyle}>
                        <FormattedMessage
                            defaultMessage="Enter the password associated with your account to sign in."
                            id="login_form.password.enter_the_password"
                        />
                    </Text>
                )}
            </Stack>
            <Stack justify="center" align="center" display={!hideEmail ? 'initial' : 'none'}>
                <ContentRenderer
                    contentIds="create-loyalty-benefits"
                    sx={loyaltyBenefitsStyle}
                    isSlot={false}
                />
            </Stack>
            <form
                id="login-form"
                onSubmit={form.handleSubmit(submitForm)}
                data-testid="sf-auth-modal-form"
                noValidate
            >
                {form?.formState?.errors?.global?.message &&
                    !form?.formState?.errors?.global?.message?.includes('Password incorrect') && (
                        <Alert status="error" mt={pxToRem(10)}>
                            <AlertIcon color="red.500" boxSize={4} />
                            <Text fontSize="sm" ml={3} mb={0}>
                                {form?.formState?.errors?.global?.message}
                            </Text>
                        </Alert>
                    )}
                <Stack spacing={6} sx={loginFormStyle}>
                    {isPasswordlessEnabled ? (
                        <PasswordlessLogin
                            form={form}
                            handleForgotPasswordClick={handleForgotPasswordClick}
                            handlePasswordlessLoginClick={handlePasswordlessLoginClick}
                            isSocialEnabled={isSocialEnabled}
                            idps={idps}
                            setLoginType={setLoginType}
                        />
                    ) : (
                        <StandardLogin
                            form={form}
                            handleEmailSubmit={handleEmailSubmit}
                            handleEditEmailClick={handleEditEmailClick}
                            isCustomerSearchLoading={isCustomerSearchLoading}
                            handleForgotPasswordClick={handleForgotPasswordClick}
                            isSocialEnabled={isSocialEnabled}
                            hideEmail={hideEmail}
                            hidePassword={hidePassword}
                            idps={idps}
                        />
                    )}
                </Stack>
            </form>
        </Fragment>
    )
}

LoginForm.propTypes = {
    hideEmail: PropTypes.bool,
    hidePassword: PropTypes.bool,
    handleEditEmailClick: PropTypes.func,
    submitForm: PropTypes.func,
    handleEmailSubmit: PropTypes.func,
    isCustomerSearchLoading: PropTypes.bool,
    handleForgotPasswordClick: PropTypes.func,
    clickCreateAccount: PropTypes.func,
    handlePasswordlessLoginClick: PropTypes.func,
    form: PropTypes.object,
    isPasswordlessEnabled: PropTypes.bool,
    isSocialEnabled: PropTypes.bool,
    idps: PropTypes.arrayOf(PropTypes.string),
    setLoginType: PropTypes.func
}

export default LoginForm
